import React, { useState, useEffect } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSafeNavigate, useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { editeursAPI } from '../services/api';

function ModifierEditeurPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const { allowNextNavigation, setHasUnsavedChanges: setGlobalHasUnsavedChanges } = useUnsavedChangesContext();

  // Détecter si c'est une création ou une modification
  const isCreating = !id || id === 'ajouter';

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset } = useForm({
    defaultValues: {
      nom: '',
      adresse: '',
      site_web: ''
    }
  });

  // Récupérer les données de l'éditeur (seulement en mode modification)
  const { data: editeur, isLoading, error } = useQuery(
    ['editeur', id],
    () => editeursAPI.getById(id).then(res => res.data.data),
    {
      enabled: !isCreating && !!id
    }
  );

  // Surveiller les modifications du formulaire
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
    setGlobalHasUnsavedChanges(isDirty, t(isCreating ? 'ajouterEditeurPage.unsavedChangesMessage' : 'modifierEditeurPage.unsavedChangesMessage'));
  }, [isDirty, setGlobalHasUnsavedChanges, isCreating, t]);

  // Pré-remplir le formulaire quand les données sont disponibles (mode modification)
  useEffect(() => {
    if (editeur && !isCreating) {
      reset({
        nom: editeur.nom || '',
        adresse: editeur.adresse || '',
        site_web: editeur.site_web || ''
      });
    } else if (isCreating) {
      reset({
        nom: '',
        adresse: '',
        site_web: ''
      });
    }
  }, [editeur, reset, isCreating]);

  // Mutation pour créer ou mettre à jour l'éditeur
  const saveEditeurMutation = useMutation(
    (data) => {
      // Nettoyer les champs vides
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== '')
      );
      return isCreating
        ? editeursAPI.create(cleanData)
        : editeursAPI.update(id, cleanData);
    },
    {
      onSuccess: (response) => {
        // Réinitialiser les modifications non sauvegardées et autoriser la navigation
        allowNextNavigation();
        setGlobalHasUnsavedChanges(false);
        
        if (isCreating) {
          toast.success(t('ajouterEditeurPage.createSuccess'));
          queryClient.invalidateQueries('editeurs');
          navigate(`/editeurs/${response.data.data.id}`);
        } else {
          toast.success(t('modifierEditeurPage.updateSuccess'));
          queryClient.invalidateQueries(['editeur', id]);
          queryClient.invalidateQueries('editeurs');
          queryClient.invalidateQueries('editeurs-list');
          navigate(`/editeurs/${id}`);
        }
      },
      onError: (error) => {
        console.error(`Erreur lors de la ${isCreating ? 'création' : 'modification'}:`, error);
        
        if (error.response?.status === 409) {
          toast.error(isCreating ? t('ajouterEditeurPage.alreadyExists') : t('modifierEditeurPage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(isCreating ? t('ajouterEditeurPage.createError') : t('modifierEditeurPage.updateError'));
        }
      }
    }
  );

  const onSubmit = (data) => {
    saveEditeurMutation.mutate(data);
  };

  const handleBack = () => {
    navigate(isCreating ? '/editeurs' : `/editeurs/${id}`);
  };

  if (!isCreating && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isCreating && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierEditeurPage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  if (!isCreating && !editeur) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierEditeurPage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
              }}
            >
              {isCreating ? t('ajouterEditeurPage.title') : t('modifierEditeurPage.title')}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              startIcon={saveEditeurMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saveEditeurMutation.isLoading}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
              }}
            >
              {saveEditeurMutation.isLoading ? t('modifierEditeurPage.saving') : t('modifierEditeurPage.save')}
            </Button>
          </Box>
        </Box>

        {/* Formulaire */}
        <Controller
          name="nom"
          control={control}
          rules={{ 
            required: t(isCreating ? 'ajouterEditeurPage.nameRequired' : 'modifierEditeurPage.nameRequired'),
            minLength: { value: 1, message: t(isCreating ? 'ajouterEditeurPage.nameMinLength' : 'modifierEditeurPage.nameMinLength') }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t(isCreating ? 'ajouterEditeurPage.nameLabel' : 'modifierEditeurPage.nameLabel')}
              margin="normal"
              error={!!errors.nom}
              helperText={errors.nom?.message}
              disabled={saveEditeurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="adresse"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t(isCreating ? 'ajouterEditeurPage.addressLabel' : 'modifierEditeurPage.addressLabel')}
              margin="normal"
              multiline
              rows={2}
              disabled={saveEditeurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="site_web"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t(isCreating ? 'ajouterEditeurPage.websiteLabel' : 'modifierEditeurPage.websiteLabel')}
              margin="normal"
              placeholder={t(isCreating ? 'ajouterEditeurPage.websitePlaceholder' : 'modifierEditeurPage.websitePlaceholder')}
              disabled={saveEditeurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />
      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t(isCreating ? 'ajouterEditeurPage.unsavedChangesMessage' : 'modifierEditeurPage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierEditeurPage;

